/**
 * Split Lines Component
 * Extracted from Stretch theme and adapted for current theme
 * Splits text into lines and optionally preserves individual letters
 */

class SplitLines extends HTMLElement {
  constructor() {
    super();
    this.requireSplit = true;
    this.lastScreenWidth = window.innerWidth;
    
    // Create shadow DOM
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>"));
    
    // Add resize listener with throttling
    window.addEventListener("resize", this.throttle(this.onWindowResized.bind(this), 100));
    
    // Watch for content changes
    new MutationObserver(this.split.bind(this, true)).observe(this, { 
      characterData: true, 
      attributes: false, 
      childList: false, 
      subtree: true 
    });
  }

  connectedCallback() {
    if (this.hasAttribute("split-on-insert")) {
      this.split();
    }
  }

  get lines() {
    this.split();
    return Array.from(this.shadowRoot.children);
  }

  get preserveLetters() {
    return this.hasAttribute("preserve-letters");
  }

  split(force = false) {
    if (!this.requireSplit && !force) {
      return;
    }

    // Split text into individual letters
    this.shadowRoot.innerHTML = this.textContent
      .replace(/./g, '<span part="letter">$&</span>')
      .replace(/\s/g, " ");

    // Group letters by line (based on their vertical position)
    const bounds = new Map();
    Array.from(this.shadowRoot.children).forEach((letter) => {
      const key = Math.round(letter.offsetTop);
      if (this.preserveLetters) {
        bounds.set(key, (bounds.get(key) || "").concat(letter.outerHTML));
      } else {
        bounds.set(key, (bounds.get(key) || "").concat(letter.textContent));
      }
    });

    // Create line elements
    this.shadowRoot.replaceChildren(...Array.from(bounds.values(), (line) => {
      return document.createRange().createContextualFragment(
        `<span part="line" style="display: inline-block; line-height: 1.2;">${line}</span>`
      );
    }));

    this.requireSplit = false;
    this.classList.add("is-split");
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent("split-lines:split", { 
      bubbles: true, 
      detail: { lines: Array.from(this.shadowRoot.children) } 
    }));
  }

  onWindowResized() {
    if (this.lastScreenWidth === window.innerWidth) {
      return;
    }
    
    this.split(true);
    this.lastScreenWidth = window.innerWidth;
  }

  // Simple throttle function
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// Register the custom element
if (!window.customElements.get("split-lines")) {
  window.customElements.define("split-lines", SplitLines);
}
